import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart3, CheckCircle, AlertCircle, Building2 } from "lucide-react";

import { API } from "../lib/api";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch(() => setError("Invalid or expired invitation link"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!user) {
      navigate(`/auth?invite=${token}`);
      return;
    }
    setAccepting(true);
    try {
      await axios.post(`${API}/api/invite/${token}/accept`, {}, { withCredentials: true });
      if (refreshUser) await refreshUser();
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <img src="/synq-mark.png" alt="SYNQ" className="w-9 h-9 object-contain" draggable={false} />
          <span className="font-outfit text-xl font-bold text-slate-900">SYNQ</span>
        </div>

        {error ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-outfit text-xl font-bold text-slate-900 mb-2">Invitation Error</h2>
            <p className="font-ibm-plex text-sm text-slate-500 mb-6">{error}</p>
            <button onClick={() => navigate("/auth")}
              className="bg-slate-900 text-white font-outfit font-semibold px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors">
              Go to Sign In
            </button>
          </div>
        ) : invite ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center animate-slide-up">
            <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <Building2 className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="font-outfit text-2xl font-black text-slate-900 mb-2">
              You're invited to join
            </h2>
            <p className="font-outfit text-lg font-bold text-blue-600 mb-1">{invite.org_name}</p>
            <p className="font-ibm-plex text-sm text-slate-500 mb-6">
              {invite.employee_name} ({invite.employee_email})
            </p>
            {invite.status === "accepted" ? (
              <div className="flex items-center justify-center gap-2 text-emerald-600 font-ibm-plex text-sm">
                <CheckCircle className="w-5 h-5" />
                Invitation already accepted
              </div>
            ) : (
              <button
                data-testid="accept-invite-btn"
                onClick={handleAccept}
                disabled={accepting}
                className="w-full bg-slate-900 text-white font-outfit font-semibold py-3 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-60"
              >
                {accepting ? "Joining..." : user ? "Accept & Join" : "Sign In to Accept"}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
